import get from 'lodash/get';
import { MICRO_SITE_ENDPOINTS, QUESTION_IDS } from 'consts';
import {
  isQuestionOptionsCacheable,
  isSessionStorageEnabled,
} from 'utils/generalUtils';
import { TRIAD_PROXY_ROUTE } from 'app-requests/apiConstants';
import { questionsToMap } from 'utils/formValuesUtils';
import {
  getQuestionOptionsPayload,
  GetQuestionOptionsParsedResponse,
  GetQuestionOptionsRawResponse,
} from 'types';
import { LogWarning } from 'utils/logging';
import { getUserSessionId } from 'utils/analyticsHelpers';
import { triadSuperFetch } from 'utils/superFetch';

const { SUBJECT_0F_INTEREST } = QUESTION_IDS;

/**
 * @summary given the current state of the form and the current school we may need to get options for a question
 */
export async function getQuestionOptions(
  payload: getQuestionOptionsPayload,
  schoolCode: string,
  variant: string,
  endpoint = MICRO_SITE_ENDPOINTS.QUESTION_OPTIONS_ENDPOINT,
  pageTaxonomyValues = {}
): Promise<GetQuestionOptionsParsedResponse> {
  const replies = get(payload, 'questionReplies', []).map(
    ({ questionAnswer, questionId }) => ({
      questionAnswer,
      questionId,
    })
  );

  const neededOptions = get(payload, 'requestedOptions', []);
  const endpointUrl = `${TRIAD_PROXY_ROUTE}${endpoint}`;
  let cacheKey = '';

  // if options request is just for subject then cache
  if (isQuestionOptionsCacheable(neededOptions)) {
    // not taking variant into account here because we are only caching for subject which is the same on all variants
    cacheKey = `${schoolCode}_questionOptions_${SUBJECT_0F_INTEREST}`;
    let cachedValue;

    if (isSessionStorageEnabled()) {
      cachedValue = sessionStorage.getItem(cacheKey);
    }

    // remove bad cache
    if (cachedValue) {
      const parsedCachedValue = JSON.parse(cachedValue);
      const questionsInCache = parsedCachedValue?.questions;
      const optionsInCache = questionsInCache?.[0]?.options?.[0]?.options;

      if (questionsInCache?.length === 0) {
        LogWarning(`Cached value found for ${cacheKey} but it has no options`);
        sessionStorage.removeItem(cacheKey);
      }

      if (optionsInCache?.length === 0) {
        LogWarning(`Cached value found for ${cacheKey} but it has no options`);
        sessionStorage.removeItem(cacheKey);
      }
    }
  }

  if (!getUserSessionId() && !isQuestionOptionsCacheable(neededOptions)) {
    LogWarning('getQuestionOptions: No user session id found');
  }

  const [apiError, apiResponse] =
    await triadSuperFetch<GetQuestionOptionsRawResponse>(endpointUrl, {
      method: 'POST',
      timeout: 10000,
      body: {
        schoolCode,
        templateName: variant,
        requestedOptions: neededOptions,
        questionReplies: replies,
        pageTaxonomyValues,
      },
      cache: {
        type: 'sessionStorage',
        key: cacheKey,
        shouldCache: (response: GetQuestionOptionsRawResponse) => {
          return response?.questions?.length > 0;
        },
      },
      retry: {
        maxAttempts: 3,
        waitTimeBetweenFails: 1000,
      },
    });

  if (apiError) {
    throw apiError;
  }

  return questionsToMap(apiResponse.questions);
}
